import { useParams } from "react-router-dom";
import { Header, Section } from "../../../components/layout";
import {
  Editable,
  GoogleMapsLink,
  RecordMissing,
  InfoTooltip,
} from "../../../components/content";
import {
  commaSeparated,
  hasAdminLevelPriviledge,
  ExternalTeamIDInfo,
} from "../../../helpers";
import {
  emailExistSchema,
  fieldRequiredSchema,
  nameSchema,
  phoneNumberSchema,
} from "../../../validations/fields.schemas";
import {
  CircularProgress,
  Link,
  TableRow,
  TableCell,
  TableRowSubhead,
} from "../../../components/util";
import AdminService, { IUser } from "../../../services/admin/admin.service";
import { useContext } from "react";
import { AuthContext } from "../../../contexts";
import industriesService, {
  Industry,
} from "../../../services/industries/industries.service";
import * as Styled from "./styles.css";
import { capitalizeFirstLetter } from "../../../validations/utilities";
import { useOrgQuery } from "../../../hooks/queries/useOrgQuery";
import { uuid } from "../../../types";

const General = () => {
  const auth = useContext(AuthContext);
  const { orgId } = useParams<IParams>();

  const {
    isLoading,
    isError,
    data: org,
    refetch: refetchOrgDetails,
  } = useOrgQuery(orgId);

  if (isLoading) {
    return <CircularProgress contained />;
  } else if (isError || org == null) {
    return (
      <RecordMissing
        icon="organization"
        message="Please check the organisation id or navigate back to Organisations."
        type="organisation"
        back={{
          title: "Back to Organisations",
          path: "/organisations",
        }}
      />
    );
  }

  const evariId = org.derived?.evariId;

  return (
    <Styled.Grid>
      <Section>
        <Header title="Organisation" type="section" />
        <Styled.Table
          columns={[
            { title: "Key", expand: true },
            { title: "Value", expand: true },
          ]}
          orientation="horizontal"
        >
          <TableRowSubhead>Organisation details</TableRowSubhead>
          <TableRow>
            <TableCell>Type</TableCell>
            <TableCell>
              <Styled.OrgTypeChip
                label={capitalizeFirstLetter(org.type)}
                size="md"
                color="positive"
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Organisation name</TableCell>
            <TableCell>
              <Editable
                field="name"
                fieldValue={org.name}
                hasPermission={hasAdminLevelPriviledge(auth.user?.roles || [])}
                record={org}
                recordType={`Organisation ${org.name} successfully`}
                onSubmitService={AdminService.updateOrgaisation}
                onSubmitRefresh={refetchOrgDetails}
                validationSchema={fieldRequiredSchema("Org name")}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>ABN</TableCell>
            <TableCell>
              <Editable
                field="abn"
                fieldValue={org.abn}
                hasPermission={hasAdminLevelPriviledge(auth.user?.roles || [])}
                record={org}
                recordType={`Organisation ${org.name} successfully`}
                onSubmitService={AdminService.updateOrgaisation}
                onSubmitRefresh={refetchOrgDetails}
                validationSchema={fieldRequiredSchema("ABN")}
              />
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell>Industry</TableCell>
            <TableCell>
              <Editable
                field="industry_id"
                fieldValue={org?.industry?.name}
                getList={industriesService.getAllIndustries}
                listPruner={industrySelectPruner}
                hasPermission={hasAdminLevelPriviledge(auth.user?.roles || [])}
                record={org}
                recordType={`Organisation ${org.name} successfully`}
                onSubmitService={AdminService.updateOrgaisation}
                onSubmitRefresh={refetchOrgDetails}
                validationSchema={fieldRequiredSchema("Industry")}
              />
            </TableCell>
          </TableRow>

          <TableRowSubhead>Contact details</TableRowSubhead>

          <TableRow>
            <TableCell>Email</TableCell>
            <TableCell>
              <Editable
                field="email"
                fieldValue={
                  <Link href={`mailto:${org.email}`}>{org.email}</Link>
                }
                hasPermission={hasAdminLevelPriviledge(auth.user?.roles || [])}
                onSubmitService={AdminService.updateOrgaisation}
                onSubmitRefresh={refetchOrgDetails}
                record={org}
                recordType={`Organisation ${org.name} successfully`}
                validationSchema={emailExistSchema}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Phone number</TableCell>
            <TableCell>
              <Editable
                field="phone"
                fieldValue={<Link href={`tel:${org.phone}`}>{org.phone}</Link>}
                hasPermission={hasAdminLevelPriviledge(auth.user?.roles || [])}
                onSubmitService={AdminService.updateOrgaisation}
                onSubmitRefresh={refetchOrgDetails}
                record={org}
                recordType={`Organisation ${org.name} successfully`}
                validationSchema={phoneNumberSchema}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <Styled.AddressCell>Address </Styled.AddressCell>
            <Styled.AddressCell>
              <Editable
                address
                fieldValue={
                  <GoogleMapsLink
                    address={commaSeparated([
                      org.address_1,
                      org.address_2,
                      org.city,
                      org.state,
                      org.post_code,
                      org.country,
                    ])}
                  />
                }
                field="address"
                hasPermission={hasAdminLevelPriviledge(auth.user?.roles || [])}
                onSubmitService={AdminService.updateOrgaisation}
                onSubmitRefresh={refetchOrgDetails}
                record={org}
                recordType={`Organisation ${org.name} successfully`}
                validationSchema={nameSchema}
              />
            </Styled.AddressCell>
          </TableRow>
        </Styled.Table>
      </Section>

      <Section>
        <Header title="Connections" type="section" />
        <Styled.Table
          columns={[
            { title: "Key", expand: true },
            { title: "Value", expand: true },
          ]}
          orientation="horizontal"
        >
          <TableRow>
            <TableCell>
              External Team ID
              <InfoTooltip msg={ExternalTeamIDInfo} />
            </TableCell>
            <TableCell>
              <Editable
                field="evari"
                fieldValue={evariId}
                hasPermission={hasAdminLevelPriviledge(auth.user?.roles || [])}
                record={{ ...org, evari: org.derived?.evariId || "" }}
                recordType={`Organisation ${org.name} successfully`}
                onSubmitService={AdminService.updateOrgaisation}
                onSubmitRefresh={refetchOrgDetails}
                validationSchema={fieldRequiredSchema("Evari Team ID")}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>Policy Number</TableCell>
            <TableCell>
              <Editable
                field="policy"
                fieldValue={org.derived?.policyNumber}
                hasPermission={hasAdminLevelPriviledge(auth.user?.roles || [])}
                record={{ ...org, policy: org.derived?.policyNumber || "" }}
                recordType={`Organisation ${org.name} successfully`}
                onSubmitService={AdminService.updateOrgaisation}
                onSubmitRefresh={refetchOrgDetails}
                validationSchema={fieldRequiredSchema("Policy Number")}
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell>CRM ID</TableCell>
            <TableCell>{org.crm_id}</TableCell>
          </TableRow>
        </Styled.Table>
      </Section>
    </Styled.Grid>
  );
};
interface IParams {
  orgId: uuid;
}

interface IUsersProps {
  orgName: string;
  users: IUser[] | undefined;
}

export default General;

export const industrySelectPruner: any = (industry: Industry[]) => {
  const result = industry.map((industry: Industry) => {
    return { title: industry.name, value: industry.id };
  });
  return result;
};
